import ApprovePage from 'pages/Approve';
import DataUploadPage from 'pages/DataUpload/pages/DataUpload';
import DataUploadListPage from 'pages/DataUpload/pages/DataUploadList';
import DataUploadMergePage from 'pages/DataUpload/pages/DataUploadMerge';
import DeliveryPortalLandingPage from 'pages/DeliveryPortal/pages/DeliveryPortalLandingPage';
import DeliveryPortalUploadPage from 'pages/DeliveryPortal/pages/DeliveryPortalUpload';
import DeliveryPortalUploadList from 'pages/DeliveryPortal/pages/DeliveryPortalUploadList';
import { DevicePage, EditDevicePage } from 'pages/Device';
import DraftsPage from 'pages/Drafts';
import HomePage from 'pages/Home';
import ManageSerialNumbers from 'pages/ManageSerialNumbers';
import NotFound from 'pages/NotFound';
import QualityRecordDownload from 'pages/QualityRecordDownload/QualityRecordDownload';
import ReviewPage from 'pages/Review';
import { SATOSetsPage, SATOTraysPage, SATOVideosPage } from 'pages/SATO';
import SearchPage from 'pages/Search';
import SerialNumberHistory from 'pages/SerialNumberHistory';
import { EditSetPage, SetPage } from 'pages/Set';
import UserManagement from 'pages/UserManagement';
import { AppRoute } from 'typings/AppRoute';
import { adminRoles, entryRoles, reviewerRoles, satoRoles } from 'utils/helpers/permissions.helpers';

type RouteName =
  | 'Home'
  | 'Drafts'
  | 'Search'
  | 'SetEdit'
  | 'Set'
  | 'DeviceEdit'
  | 'Device'
  | 'Review'
  | 'Approve'
  | 'SATOSets'
  | 'SATOTrays'
  | 'SATOVideos'
  | 'UserManagement'
  | 'DeliveryPortalList'
  | 'DeliveryPortalUpload'
  | 'DeliveryPortalFilter'
  | 'DeliveryPortalUploadList'
  | 'DataUpload'
  | 'DataUploadList'
  | 'DataUploadMerge'
  | 'QualityRecordsReport'
  | 'NotFound'
  | 'ManageSetSerialNumbers'
  | 'SerialNumberHistory';

type IRoutes = Record<RouteName, AppRoute>;

const Routes: IRoutes = {
  Home: {
    title: 'Home',
    path: '/',
    makePath: () => '/',
    exact: true,
    component: HomePage,
  },
  Drafts: {
    title: 'Draft',
    path: '/drafts',
    makePath: () => `/drafts`,
    exact: true,
    component: DraftsPage,
    roles: entryRoles,
  },
  Search: {
    title: 'Search',
    path: '/search',
    makePath: () => `/search`,
    exact: true,
    component: SearchPage,
    roles: entryRoles,
  },
  SetEdit: {
    title: 'Set Edit',
    path: '/set/:code/edit',
    makePath: (code: string) => `/set/${code}/edit`,
    component: EditSetPage,
    roles: entryRoles,
  },
  SerialNumberHistory: {
    title: 'Serial Number History',
    path: '/set/:code/manage-serial-numbers/:setUssId/history',
    makePath: (code: string, setUssId: string) => `/set/${code}/manage-serial-numbers/${setUssId}/history`,
    component: SerialNumberHistory,
    roles: entryRoles,
  },
  ManageSetSerialNumbers: {
    title: 'Manage Serial Numbers',
    path: '/set/:code/manage-serial-numbers',
    makePath: (code: string) => `/set/${code}/manage-serial-numbers`,
    component: ManageSerialNumbers,
    roles: entryRoles,
  },
  Set: {
    title: 'Set (View Only)',
    path: '/set/:code',
    makePath: (code: string) => `/set/${code}`,
    component: SetPage,
    roles: entryRoles,
  },
  DeviceEdit: {
    title: 'Device Edit',
    path: '/device/:code/edit',
    makePath: (code: string) => `/device/${code}/edit`,
    component: EditDevicePage,
    roles: entryRoles,
  },
  Device: {
    title: 'Device (View Only)',
    path: '/device/:code',
    makePath: (code: string) => `/device/${code}`,
    component: DevicePage,
    roles: entryRoles,
  },
  Review: {
    title: 'Review',
    path: '/review',
    makePath: () => `/review`,
    component: ReviewPage,
    roles: reviewerRoles,
  },
  Approve: {
    title: 'Approve',
    path: '/approve',
    makePath: () => `/approve`,
    component: ApprovePage,
    roles: reviewerRoles,
  },
  /* All SATO routes are only visible to MK ML Engineer users */
  SATOVideos: {
    title: 'Videos',
    path: '/sato/:setCode/:trayId',
    makePath: (setCode: string, trayId: string) => `/sato/${setCode}/${trayId}`,
    exact: true,
    component: SATOVideosPage,
    roles: satoRoles,
  },
  SATOTrays: {
    title: 'Trays',
    path: '/sato/:setCode',
    makePath: (setCode: string) => `/sato/${setCode}`,
    exact: true,
    component: SATOTraysPage,
    roles: satoRoles,
  },
  SATOSets: {
    title: 'Sets Pending Review',
    path: '/sato',
    makePath: () => `/sato`,
    exact: true,
    component: SATOSetsPage,
    roles: satoRoles,
  },
  UserManagement: {
    title: 'User Management',
    path: '/user-management',
    makePath: () => '/user-management',
    exact: true,
    component: UserManagement,
    roles: adminRoles,
  },
  DeliveryPortalList: {
    title: 'Delivery Portal',
    path: '/delivery-portal',
    makePath: () => '/delivery-portal',
    exact: true,
    component: DeliveryPortalLandingPage,
    roles: adminRoles,
    featureFlag: 'DELIVERY_PORTAL',
  },
  DeliveryPortalUpload: {
    title: 'Delivery Portal: File Upload',
    path: '/delivery-portal/upload',
    makePath: () => '/delivery-portal/upload',
    exact: true,
    component: DeliveryPortalUploadPage,
    roles: adminRoles,
    featureFlag: 'DELIVERY_PORTAL',
  },
  DeliveryPortalFilter: {
    title: 'Delivery Portal: Filter',
    path: '/delivery-portal/filter',
    makePath: () => '/delivery-portal/filter',
    exact: true,
    component: DeliveryPortalUploadPage,
    roles: adminRoles,
    featureFlag: 'DELIVERY_PORTAL',
  },
  DeliveryPortalUploadList: {
    title: 'Delivery Portal: List',
    path: '/delivery-portal/upload-list',
    makePath: () => '/delivery-portal/upload-list',
    exact: true,
    component: DeliveryPortalUploadList,
    roles: adminRoles,
    featureFlag: 'DELIVERY_PORTAL',
  },
  DataUpload: {
    title: 'Data Upload: Upload',
    path: '/data-upload/upload',
    makePath: () => '/data-upload/upload',
    exact: true,
    component: DataUploadPage,
    roles: adminRoles,
  },
  DataUploadList: {
    title: 'Data Upload: List',
    path: '/data-upload/list',
    makePath: () => '/data-upload/list',
    exact: true,
    component: DataUploadListPage,
    roles: adminRoles,
  },
  DataUploadMerge: {
    title: 'Data Upload: Merge',
    path: '/data-upload/merge/:uploadType/:uploadId',
    makePath: () => '/data-upload/merge/:uploadType/:uploadId',
    component: DataUploadMergePage,
    roles: adminRoles,
  },
  QualityRecordsReport: {
    title: 'Quality Records',
    path: '/reports/quality_record/:deliveryCode/:setCode',
    makePath: () => '/reports/quality_record/:deliveryCode/:setCode',
    component: QualityRecordDownload,
    roles: adminRoles,
  },
  NotFound: {
    title: '404',
    path: '/404',
    makePath: () => `/404`,
    exact: true,
    component: NotFound,
    insecure: true,
  },
};

export default Routes;
